import React, { createContext, useContext, useState, useEffect } from "react";

interface ProviderData {
  consigneeId: string;
  dealerAcknowlegement: boolean;
  informUserMyself: boolean;
  providePermobilData: boolean;
  noConsigneeId?: boolean;
}

interface FormContextType {
  providerFormData: ProviderData;
  setFormData: React.Dispatch<React.SetStateAction<ProviderData>>;
  clearFormData: () => void;
}

const STORAGE_KEY = "providerFormData";

const MultiStepFormContext = createContext<FormContextType | undefined>(
  undefined
);

export const useMultiStepForm = () => {
  const context = useContext(MultiStepFormContext);
  if (!context)
    throw new Error(
      "useMultiStepForm must be used within MultiStepFormProvider"
    );
  return context;
};

export const MultiStepFormProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [providerFormData, setProviderFormData] = useState<ProviderData>(() => {
    try {
      const storedData = sessionStorage.getItem(STORAGE_KEY);
      return storedData
        ? JSON.parse(storedData)
        : {
            consigneeId: "",
            noConsigneeId: undefined,
            dealerAcknowlegement: false,
            informUserMyself: false,
            providePermobilData: false,
          };
    } catch {
      return {
        consigneeId: "",
        noConsigneeId: undefined,
        dealerAcknowlegement: false,
        informUserMyself: false,
        providePermobilData: false,
      };
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(providerFormData));
    } catch {
      // ignore write errors
    }
  }, [providerFormData]);

  const clearFormData = () => {
    setProviderFormData({
      consigneeId: "",
      noConsigneeId: undefined,
      dealerAcknowlegement: false,
      informUserMyself: false,
      providePermobilData: false,
    });
    sessionStorage.removeItem(STORAGE_KEY);
  };

  return (
    <MultiStepFormContext.Provider
      value={{
        providerFormData,
        setFormData: setProviderFormData,
        clearFormData,
      }}
    >
      {children}
    </MultiStepFormContext.Provider>
  );
};
